/**
 * AudioBaseNode - base class for audio nodes
 *
 * Extends BaseNode with the audio proxy for connecting internal audio
 * processing to endpoint GainNodes. Uses plain Web Audio connect().
 *
 * Metadata (e.g., spatial) arriving on audio inputs is automatically
 * forwarded to all audio outputs via onInternalChanged → setValue.
 *
 * For Tone.js nodes, use ToneAudioBaseNode instead (has Tone.js bridging).
 */

import { BaseNode } from '@nodes/BaseNode';
import type { Dependencies } from '@nodes/Dependencies';
import type { EndpointsRecord, TypedAudio } from '@nodes/TypedEndpoints';
import type { IEndpoint } from '@/runtime/IEndpoint';
import type { Payload } from '@/graph/Payload';

export interface AudioDependencies extends Dependencies {
  /** AudioContext - accepts any AudioContext implementation */
  audioContext: any;
}

export abstract class AudioBaseNode<T extends EndpointsRecord = EndpointsRecord> extends BaseNode<T> {
  protected audioContext: any;

  /** Typed accessor for audio endpoints: this.audio.audioOut.connect(synth) */
  protected audio: TypedAudio<T>;

  getGainNode;

  // Audio endpoint IDs discovered at initialize() for metadata forwarding
  private audioInputIds: string[] = [];
  private audioOutputIds: string[] = [];

  constructor(
    id: string,
    deps: AudioDependencies
  ) {
    super(id, deps);
    this.audioContext = deps.audioContext;

    this.getGainNode = deps.endpointApi.getGainNode

    // Create typed proxy for audio endpoints
    this.audio = new Proxy({} as TypedAudio<T>, {
      get: (_, key: string) => {
        const metadata = deps.endpointApi.getMetadata(key);
        if (!metadata) throw new Error(`[${this.id}] audio '${key}' does not exist`);
        if (metadata.dataType !== 'audio') {
          throw new Error(`[${this.id}] '${key}' is not an audio endpoint`);
        }

        const isInput = metadata.isInput;
        return {
          connect: (node: any) => {
            const gainNode = deps.endpointApi.getGainNode(key);
            if (isInput) {
              // Input: endpoint -> internal node
              gainNode.connect(node);
            } else {
              // Output: internal node -> endpoint
              node.connect(gainNode);
            }
          },
          disconnect: (node: any) => {
            if (isInput) {
              deps.endpointApi.disconnectFromInput(key, node);
            } else {
              deps.endpointApi.disconnectFromOutput(key, node);
            }
          }
        };
      }
    });
  }

  initialize(): void {
    super.initialize();
    this.discoverAudioEndpoints();
  }

  /**
   * Discover audio input/output endpoint IDs for metadata forwarding.
   */
  private discoverAudioEndpoints(): void {
    for (const id of this.deps.endpointApi.getEndpointIds()) {
      const meta = this.deps.endpointApi.getMetadata(id);
      if (meta?.dataType === 'audio') {
        if (meta.isInput) this.audioInputIds.push(id);
        if (meta.isOutput) this.audioOutputIds.push(id);
      }
    }
  }

  /**
   * Metadata arriving on an audio input endpoint.
   * Forward to all audio outputs automatically (passthrough).
   * Subclasses can override to consume instead of forwarding.
   */
  onInternalChanged(payload: Payload, endpointId: string, source: IEndpoint): void {
    if (this.audioInputIds.includes(endpointId) && this.audioOutputIds.length > 0) {
      for (const outputId of this.audioOutputIds) {
        this.deps.endpointApi.sendOutput(outputId, payload);
      }
    }
  }
}
